import Validator from '@shared/validatorjs/src/validator'
import {
  getCreateMobileUserState,
  getUpdateMobileUserState,
} from '../mobile-users.selectors'
import {
  UpdateField,
  CreateMobileUserRequest,
  SetDefaultData,
} from '../mobile-users.actions'
import { Store, select } from '@ngrx/store'
import { Component, OnInit, OnDestroy } from '@angular/core'
import FormTrait from 'src/app/traits/form.trait'
import { Map } from 'immutable'
import { debounceTime, Subject, Subscription } from 'rxjs'
import {
  getLayerFeaturesState,
  getMobileUserModesState,
} from '@shared/shared.select'
import {
  FetchLayerFeaturesRequest,
  FetchMobileUserModesRequest,
} from '@shared/shared.actions'
import { FetchMobileSettingsRequest } from '@containers/mobile-settings/mobile-settings.actions'
import { getMobileSettingsState } from '@containers/mobile-settings/mobile-settings.selectors'
import { TranslateService } from '@ngx-translate/core'

@Component({
  selector: 'app-create-mobile-user',
  templateUrl: './create-mobile-user.component.html',
  styleUrls: ['./create-mobile-user.component.css'],
})
export class CreateMobileUserComponent
  extends FormTrait
  implements OnInit, OnDestroy
{
  private state$: Subscription
  public isLoading: boolean
  public layerFeatures: any[] = []
  public mobileSettings$: Subscription
  public layerFeatures$: Subscription
  public activeLayerId: any
  public zonesAutocomplete$: Subscription
  public onZonesAutocomplete$ = new Subject<string>()
  public mobileUserModes = []
  public mobileUserModeKeys = []
  public mobileUserModeDefaults = {}
  public mobileUserModes$: Subscription

  constructor(
    public store: Store<any>,
    private translateService: TranslateService,
  ) {
    super()
  }

  ngOnInit() {
    this.store.dispatch<any>(new SetDefaultData('createUser'))

    this.state$ = this.store
      .pipe(select(getCreateMobileUserState))
      .subscribe((state) => {
        this.state = state
        this.isLoading = state.get('isLoading')
      })

    this.layerFeatures$ = this.store
      .pipe(select(getLayerFeaturesState))
      .subscribe((state) => {
        this.layerFeatures = state.get('data').toJS()
      })

    this.mobileUserModes$ = this.store
      .pipe(select(getMobileUserModesState))
      .subscribe((state) => {
        const modes = state.toJS()

        this.mobileUserModes = modes.map((mode) => ({
          ...mode,
          title: this.translateService.instant(mode.translationKey, {
            defaultValue: mode.title,
          }),
        }))

        this.mobileUserModeKeys = modes.map((mode) => mode.translationKey)
        this.mobileUserModeDefaults = modes.reduce((prev, current) => {
          return { ...prev, [current.translationKey]: current.title }
        }, {})
      })

    this.mobileSettings$ = this.store
      .pipe(select(getMobileSettingsState))
      .subscribe((state) => {
        this.activeLayerId = state.getIn([
          'data',
          'SETTINGS_MOBILE_RESPONSIBILITY_LAYER',
        ])

        if (this.activeLayerId) {
          this.store.dispatch<any>(
            new FetchLayerFeaturesRequest({
              filters: {
                matchOr: [
                  {
                    field: 'layer.id',
                    value: this.activeLayerId,
                  },
                ],
              },
              pageSize: 100,
            }),
          )
        }
      })

    this.store.dispatch(new FetchMobileSettingsRequest())
    this.store.dispatch(new FetchMobileUserModesRequest())

    this.zonesAutocomplete$ = this.onZonesAutocomplete$
      .pipe(debounceTime(500))
      .subscribe((keyword) => {
        this.store.dispatch<any>(
          new FetchLayerFeaturesRequest({
            filters: {
              matchAnd: [
                {
                  field: 'layer.id',
                  value: this.activeLayerId,
                },
              ],
              or: [{ field: 'label', value: keyword ?? '' }],
            },
            pageSize: 50,
          }),
        )
      })
  }

  ngOnDestroy() {
    this.state$.unsubscribe()
    this.zonesAutocomplete$.unsubscribe()
    this.mobileUserModes$.unsubscribe()
  }

  public handleFieldChange(e: any, name: null | string = null): void {
    this.store.dispatch(
      new UpdateField(
        name || e.target.name,
        name ? e : e.target.value,
        'createUser.data',
      ),
    )

    this.store.dispatch(
      new UpdateField(
        'validationErrors',
        this.state.get('validationErrors').delete(name || e.target.name),
        'createUser',
      ),
    )
  }

  validation() {
    const data = this.state.get('data')
    const rules = {
      username: 'required',
      firstName: 'required',
      lastName: 'required',
      mode: 'required',
    }
    const validator = new Validator(
      {
        username: data.get('username'),
        firstName: data.get('firstName'),
        lastName: data.get('lastName'),
        mode: data.get('mode'),
      },
      rules,
    )

    validator.setAttributeNames({
      firstName: 'first name',
      lastName: 'last name',
    })

    return validator
  }

  public onSubmit(e: Event): void {
    e.preventDefault()

    const validation = this.validation()
    const payload = this.state.get('data').toJS()

    if (validation.passes()) {
      this.store.dispatch(new CreateMobileUserRequest(payload))
    } else {
      this.store.dispatch<any>(
        new UpdateField(
          'validationErrors',
          validation.errors.errors,
          'createUser',
        ),
      )
    }
  }
}
